import { Button } from "@components/common/button"
import { DictType } from "@pma/types"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@components/common/alert-dialog"
import axios from "axios"
import { parseCookies } from "nookies"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "@hooks/use-toast"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { TASK_DELETE_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"
import { Trash2Icon } from "lucide-react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import useTranslator from "@hooks/use-translator"
import { HTMLAttributes, useContext } from "react"
import { GroupedTasksContext } from "./grouped-tasks/context/GroupedTasksProvider"

interface Props extends HTMLAttributes<HTMLDivElement> {
  task?: DictType
  tasks?: Array<{ taskId: number; priorityId: number }>
}

const DeleteTask = ({ task, children, tasks }: Props) => {
  const { translate } = useTranslator()
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { setSelectedTasks } = useContext(GroupedTasksContext) as {
    setSelectedTasks: (tasks: any[]) => void
  }

  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)

  const deleteTask = async () => {
    if (tasks) {
      const promiseArr: Array<any> = []
      tasks.map(async (val) => {
        const promise = axios.delete(SERVER_URL + TASK_DELETE_ENDPOINT + `/${val.taskId}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        })
        promiseArr.push(promise)
      })
      Promise.all(promiseArr)
        .then((values) => {
          if (values[0].status === 200) {
            queryClient.invalidateQueries({ queryKey: ["get-tasks"] })
            queryClient.invalidateQueries({
              queryKey: ["clear-selected-tasks"],
            })

            queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })
            queryClient.invalidateQueries({ queryKey: ["clear-selected-group-tasks"] })
          }
          return values
        })
        .catch((error) => {
          const errorMessage = new Error(error.message)
          console.error(error.message)
          return errorMessage
        })
    } else {
      const response = await axios.delete(SERVER_URL + TASK_DELETE_ENDPOINT + `/${task?.id}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })

      return response.data
    }
  }

  const deleteTaskMutation = useMutation({
    mutationKey: ["delete-task", task?.id, tasks],
    mutationFn: deleteTask,
    onSuccess: (data) => {
      if (tasks) {
        setSelectedTasks([])
        toast({
          title: translate("Success"),
          description: translate("Selected task has been deleted"),
          variant: "success",
        })
        queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })
        queryClient.invalidateQueries({ queryKey: ["clear-selected-group-tasks"] })
        newParams.set("revalidate", "tasks")
        navigate(`?${newParams.toString()}`)
      } else {
        toast({
          title: translate("Success"),
          description: translate("task has been deleted"),
          variant: "success",
        })

        queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })
        newParams.set("revalidate", "tasks")
        navigate(`?${newParams.toString()}`)
      }
    },
    onError: (error) => {
      if (task) {
        toast({
          title: translate("Error"),
          description: `${translate("Unable to delete")} ${task?.title} ${translate("task")}`,
          variant: "error",
        })
      }

      newParams.set("revalidate", "tasks")
      navigate(`?${newParams.toString()}`)
    },
  })

  return (
    <Tooltip>
      <AlertDialog>
        <TooltipTrigger asChild>
          <AlertDialogTrigger asChild>
            {children ? (
              children
            ) : (
              <Button
                variant={"ghost"}
                className='flex h-6 w-6 flex-col items-center justify-center rounded-full text-red-400 hover:bg-slate-300'
                size={"icon"}
              >
                <Trash2Icon size={14} />
              </Button>
            )}
          </AlertDialogTrigger>
        </TooltipTrigger>
        <TooltipContent className='flex items-center gap-1 bg-destructive'>
          {
            <h4>
              {translate(`Delete`)} {task?.title} {tasks && translate("Selected tasks")}
            </h4>
          }
        </TooltipContent>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {translate("Proceed to delete")} {task?.title}
            {tasks && <span className='text-red-600'>{translate("Selected tasks")}</span>}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            {translate("This action cannot be undone")}.{" "}
            {translate("This will permanently delete the task and subitems")}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{translate("Cancel")}</AlertDialogCancel>
          <AlertDialogAction
            className='bg-destructive'
            disabled={deleteTaskMutation?.isPending}
            onClick={() => deleteTaskMutation.mutate()}
          >
            {translate("Continue")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
      </AlertDialog>
    </Tooltip>
  )
}

export default DeleteTask
